import React, { useMemo, useState } from 'react';
import {
  PropertyFeeFormRow,
  PropertyFeeInterval,
  PROPERTY_FEE_INTERVALS
} from './types';
import { CategoryDropdown, useCategoriesByType } from '../category';
import ContactSearchSection from '../contacts/ContactSearchSection';
import { ContactWithDetails } from '../contacts/types';
import { CurrencyInputField, DatePicker } from '../../../components/common';
import { getVisiblePropertyFees, todayFeeDate } from './utils/propertyFeeList';
import '../../../components/common/CRMForm.css';
import './PropertyFeesSection.css';

interface PropertyFeesSectionProps {
  propertiesFees: PropertyFeeFormRow[];
  onChange: (propertiesFees: PropertyFeeFormRow[]) => void;
}

type FeeEditor =
  | { mode: 'add' }
  | { mode: 'edit'; index: number };

const emptyFeeRow = (): PropertyFeeFormRow => ({
  id: 0,
  typeId: 0,
  description: '',
  amount: 0,
  contactId: 0,
  interval: 'monthly',
  feeDate: todayFeeDate()
});

const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const formatContactName = (fee: PropertyFeeFormRow): string => {
  const business = (fee.contactBusinessName || '').trim();
  if (business) return business;
  const last = (fee.contactLastName || '').trim();
  const first = (fee.contactFirstName || '').trim();
  if (last && first) return `${last}, ${first}`;
  if (last) return last;
  if (first) return first;
  return fee.contactId ? `Contact #${fee.contactId}` : '—';
};

const renderWrappedText = (text: string, tokenClassName: string): React.ReactNode =>
  text.split(/(\s+)/).map((part, partIndex) =>
    /^\s+$/.test(part) ? (
      <React.Fragment key={`s-${partIndex}`}>{part}</React.Fragment>
    ) : (
      <span key={`w-${partIndex}`} className={tokenClassName}>
        {part}
      </span>
    )
  );

const rowToSelectedContact = (row: PropertyFeeFormRow): ContactWithDetails | null => {
  if (!row.contactId) return null;
  return {
    id: row.contactId,
    typeId: 0,
    firstName: row.contactFirstName || '',
    lastName: row.contactLastName || '',
    businessName: row.contactBusinessName || '',
    notes: '',
    dateCreated: '',
    dateUpdated: '',
    whoUpdated: '',
    phones: [],
    emails: [],
    addresses: [],
    contactsAddresses: []
  };
};

const PropertyFeesSection: React.FC<PropertyFeesSectionProps> = ({
  propertiesFees,
  onChange
}) => {
  const { categories: feeTypes } = useCategoriesByType('propertyfee');
  const [editor, setEditor] = useState<FeeEditor | null>(null);
  const [draft, setDraft] = useState<PropertyFeeFormRow>(emptyFeeRow());
  const [draftError, setDraftError] = useState<string | null>(null);
  const [showLatest, setShowLatest] = useState(true);

  const visibleFees = useMemo(
    () => getVisiblePropertyFees(propertiesFees, showLatest),
    [propertiesFees, showLatest]
  );

  const resolveTypeName = (typeId: number, fallback?: string): string | undefined =>
    feeTypes.find(c => c.id === typeId)?.name || fallback;

  const openAdd = () => {
    setDraft(emptyFeeRow());
    setDraftError(null);
    setEditor({ mode: 'add' });
  };

  const openEdit = (index: number) => {
    const fee = propertiesFees[index];
    if (!fee) return;
    setDraft({ ...fee, feeDate: fee.feeDate || todayFeeDate() });
    setDraftError(null);
    setEditor({ mode: 'edit', index });
  };

  const closeEditor = () => {
    setEditor(null);
    setDraft(emptyFeeRow());
    setDraftError(null);
  };

  const validateDraft = (): boolean => {
    const description = draft.description.trim();
    if (!draft.typeId) {
      setDraftError('Select a fee type');
      return false;
    }
    if (!description) {
      setDraftError('Enter a fee name');
      return false;
    }
    if (description.length > 40) {
      setDraftError('Fee name must be 40 characters or less');
      return false;
    }
    if (!draft.amount || draft.amount < 0.01) {
      setDraftError('Enter an amount of at least $0.01');
      return false;
    }
    if (!draft.contactId) {
      setDraftError('Select a contact');
      return false;
    }
    if (!draft.interval) {
      setDraftError('Select a recurrence interval');
      return false;
    }
    if (!draft.feeDate || !/^\d{2}\/\d{2}\/\d{4}$/.test(draft.feeDate)) {
      setDraftError('Enter a valid fee date (mm/dd/yyyy)');
      return false;
    }
    setDraftError(null);
    return true;
  };

  const saveDraft = () => {
    if (!editor || !validateDraft()) return;

    const saved: PropertyFeeFormRow = {
      ...draft,
      description: draft.description.trim(),
      typeName: resolveTypeName(draft.typeId, draft.typeName)
    };

    if (editor.mode === 'add') {
      onChange([...propertiesFees, saved]);
    } else {
      const updated = propertiesFees.map((row, i) =>
        i === editor.index ? { ...saved, id: row.id } : row
      );
      onChange(updated);
    }
    closeEditor();
  };

  const removeFee = (index: number) => {
    if (editor?.mode === 'edit' && editor.index === index) {
      closeEditor();
    }
    onChange(propertiesFees.filter((_, i) => i !== index));
  };

  const handleContactSelect = (contact: ContactWithDetails | null) => {
    if (!contact) {
      setDraft(prev => ({
        ...prev,
        contactId: 0,
        contactFirstName: undefined,
        contactLastName: undefined,
        contactBusinessName: undefined
      }));
      return;
    }
    setDraft(prev => ({
      ...prev,
      contactId: contact.id,
      contactFirstName: contact.firstName,
      contactLastName: contact.lastName,
      contactBusinessName: contact.businessName
    }));
    setDraftError(null);
  };

  return (
    <div className="form-section-content property-fees-section">
      {editor && (
        <div className="form-item-group property-fees-editor">
          <div className="app-form-group">
            <label className="app-form-label required">Fee Type</label>
            <CategoryDropdown
              parentType="propertyfee"
              value={draft.typeId}
              onChange={(value) => {
                setDraft(prev => ({
                  ...prev,
                  typeId: value,
                  typeName: resolveTypeName(value)
                }));
                setDraftError(null);
              }}
              required={true}
              className={draft.typeId === 0 ? 'error' : ''}
              placeholder="Select fee type..."
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label required" htmlFor="property-fee-name">
              Fee Name
            </label>
            <input
              id="property-fee-name"
              type="text"
              className="app-form-input"
              value={draft.description}
              maxLength={40}
              placeholder="Enter fee name (max 40 characters)"
              onChange={(e) => {
                setDraft(prev => ({ ...prev, description: e.target.value }));
                setDraftError(null);
              }}
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label required">Fee Amount</label>
            <CurrencyInputField
              value={draft.amount}
              onChange={(amount) => {
                setDraft(prev => ({ ...prev, amount }));
                setDraftError(null);
              }}
              min={0}
              placeholder="0.00"
            />
          </div>

          <div className="app-form-group">
            <DatePicker
              label="Fee Date"
              required
              value={draft.feeDate}
              onChange={(feeDate) => {
                setDraft(prev => ({ ...prev, feeDate }));
                setDraftError(null);
              }}
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label required">Contact</label>
            <ContactSearchSection
              selectedContact={rowToSelectedContact(draft)}
              onContactSelect={handleContactSelect}
              label="Search Contact"
              placeholder="Search contacts by name..."
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label required" htmlFor="property-fee-interval">
              Recurrence
            </label>
            <select
              id="property-fee-interval"
              className="app-form-input"
              value={draft.interval}
              onChange={(e) => {
                setDraft(prev => ({ ...prev, interval: e.target.value as PropertyFeeInterval }));
                setDraftError(null);
              }}
            >
              {PROPERTY_FEE_INTERVALS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {draftError && <div className="app-form-error">{draftError}</div>}

          <div className="form-item-actions">
            <button
              type="button"
              className="app-btn app-btn-secondary"
              onClick={closeEditor}
            >
              Cancel
            </button>
            <button
              type="button"
              className="app-btn app-btn-primary"
              onClick={saveDraft}
            >
              {editor.mode === 'add' ? 'Add' : 'Update'}
            </button>
          </div>
        </div>
      )}

      {propertiesFees.length > 0 && (
        <>
          <label className="property-fees-show-latest">
            <input
              type="checkbox"
              checked={showLatest}
              onChange={(e) => setShowLatest(e.target.checked)}
            />
            Show latest
          </label>

          <div className="property-fees-ledger" role="table" aria-label="Property fees">
            <div className="property-fees-ledger-header" role="row">
              <span role="columnheader">Type</span>
              <span role="columnheader">Name</span>
              <span role="columnheader">Contact</span>
              <span role="columnheader">Date</span>
              <span role="columnheader">Amount</span>
              <span role="columnheader">Actions</span>
            </div>
            {visibleFees.map(({ fee, sourceIndex }) => (
              <div
                key={fee.id || `fee-temp-${sourceIndex}`}
                className={`property-fees-ledger-row${editor?.mode === 'edit' && editor.index === sourceIndex ? ' property-fees-ledger-row-active' : ''}`}
                role="row"
              >
                <span role="cell" className="property-fees-ledger-type">
                  {fee.typeName || resolveTypeName(fee.typeId) || `Type #${fee.typeId}`}
                </span>
                <span role="cell" className="property-fees-ledger-name">
                  {renderWrappedText(fee.description || '—', 'property-fees-ledger-token')}
                </span>
                <span role="cell" className="property-fees-ledger-contact">
                  {renderWrappedText(formatContactName(fee), 'property-fees-ledger-token')}
                </span>
                <span role="cell" className="property-fees-ledger-date">{fee.feeDate || '—'}</span>
                <span role="cell" className="property-fees-ledger-amount">{formatAmount(fee.amount)}</span>
                <span role="cell" className="property-fees-ledger-actions">
                  <button
                    type="button"
                    className="property-fees-btn-edit"
                    onClick={() => openEdit(sourceIndex)}
                    title="Edit fee"
                    aria-label="Edit fee"
                    disabled={editor !== null}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="app-btn-delete"
                    onClick={() => removeFee(sourceIndex)}
                    title="Delete fee"
                    aria-label="Delete fee"
                    disabled={editor !== null}
                  />
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {!editor && (
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={openAdd}
        >
          Add Fee
        </button>
      )}
    </div>
  );
};

export default PropertyFeesSection;
